import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { portalService } from '../../services/portal';
import type { AllSalespersonItem } from '../../types/portal';
import { useToast } from '../../hooks/useToast';
import Toast from '../../components/portal/Toast';
import styles from './PortalMySalespeoplePage.module.css';

// ── Types ──────────────────────────────────────────────────────────

type TabFilter = 'all' | 'assigned' | 'unassigned';

// ── Component ──────────────────────────────────────────────────────

export default function PortalMySalespeoplePage() {
  const navigate = useNavigate();
  const { toasts, addToast, removeToast } = useToast();

  const [salespeople, setSalespeople] = useState<AllSalespersonItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabFilter>('all');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await portalService.getAllSalespeople();
      setSalespeople(res.salespeople);
    } catch (err: unknown) {
      setError('Failed to load salespeople data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Derived counts
  const assignedCount = salespeople.filter((s) => s.assigned_to !== null).length;
  const unassignedCount = salespeople.filter((s) => s.assigned_to === null).length;
  const totalCustomers = salespeople.reduce((sum, s) => sum + s.customer_count, 0);

  // Filter salespeople by tab + search
  const filteredSalespeople = salespeople.filter((sp) => {
    // Tab filter
    if (activeTab === 'assigned' && sp.assigned_to === null) return false;
    if (activeTab === 'unassigned' && sp.assigned_to !== null) return false;

    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const name = (sp.salesperson_name || '').toLowerCase();
      const code = sp.salesperson_no.toLowerCase();
      const assignee = (sp.assigned_to_name || '').toLowerCase();
      if (!name.includes(q) && !code.includes(q) && !assignee.includes(q)) {
        return false;
      }
    }

    return true;
  });

  const handleAssign = async (code: string) => {
    setActionLoading(code);
    try {
      const result = await portalService.assignSalesperson(code);
      addToast(result.message, 'success');
      await fetchData();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Failed to assign salesperson';
      addToast(message, 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnassign = async (code: string) => {
    setActionLoading(code);
    try {
      const result = await portalService.unassignSalesperson(code);
      addToast(result.message, 'success');
      await fetchData();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Failed to unassign salesperson';
      addToast(message, 'error');
    } finally {
      setActionLoading(null);
    }
  };

  // ── Loading state ──
  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} />
        <span>Loading salespeople...</span>
      </div>
    );
  }

  // ── Error state ──
  if (error) {
    return (
      <div className={styles.error}>
        <span>{error}</span>
        <button className={styles.errorRetry} onClick={fetchData}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerRow}>
          <div>
            <h1 className={styles.title}>My Salespeople</h1>
            <p className={styles.subtitle}>
              Manage external sales representative assignments
            </p>
          </div>
        </div>
        <div className={styles.statsRow}>
          <div className={styles.stat}>
            <span className={styles.statValue}>{salespeople.length}</span>
            <span className={styles.statLabel}>Total</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statValue}>{assignedCount}</span>
            <span className={styles.statLabel}>Assigned</span>
          </div>
          {unassignedCount > 0 && (
            <div className={`${styles.stat} ${styles.statWarning}`}>
              <span className={`${styles.statValue} ${styles.statValueWarning}`}>
                {unassignedCount}
              </span>
              <span className={styles.statLabel}>Unassigned</span>
            </div>
          )}
          <div className={styles.stat}>
            <span className={styles.statValue}>{totalCustomers.toLocaleString()}</span>
            <span className={styles.statLabel}>Customers</span>
          </div>
        </div>
      </div>

      {/* Tabs + Search */}
      <div className={styles.toolbar}>
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === 'all' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('all')}
          >
            All ({salespeople.length})
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'assigned' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('assigned')}
          >
            Assigned ({assignedCount})
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'unassigned' ? styles.tabActive : ''} ${unassignedCount > 0 ? styles.tabWarning : ''}`}
            onClick={() => setActiveTab('unassigned')}
          >
            Unassigned ({unassignedCount})
          </button>
        </div>
        <div className={styles.searchWrapper}>
          <svg className={styles.searchIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            className={styles.searchInput}
            type="text"
            placeholder="Search salespeople..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Unassigned alert banner */}
      {unassignedCount > 0 && activeTab !== 'unassigned' && (
        <div className={styles.alertBanner}>
          <svg className={styles.alertIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span>
            <strong>{unassignedCount} salesperson{unassignedCount !== 1 ? 's are' : ' is'} unassigned.</strong>
            {' '}Emails from their customers won&apos;t appear in anyone&apos;s inbox.
          </span>
          <button
            className={styles.alertAction}
            onClick={() => setActiveTab('unassigned')}
          >
            View Unassigned
          </button>
        </div>
      )}

      {/* Salespeople list */}
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.thCode}>Code</th>
              <th className={styles.thName}>Name</th>
              <th className={styles.thCustomers}>Customers</th>
              <th className={styles.thAssignment}>Assigned To</th>
              <th className={styles.thActions}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredSalespeople.length === 0 ? (
              <tr>
                <td colSpan={5} className={styles.emptyRow}>
                  {searchQuery
                    ? 'No salespeople match your search'
                    : 'No salespeople found'}
                </td>
              </tr>
            ) : (
              filteredSalespeople.map((sp) => (
                <tr
                  key={sp.salesperson_no}
                  className={`${styles.row} ${sp.assigned_to === null ? styles.rowUnassigned : ''}`}
                >
                  <td className={styles.tdCode}>
                    <span className={`${styles.codeBadge} ${sp.assigned_to === null ? styles.codeBadgeUnassigned : ''}`}>
                      {sp.salesperson_no}
                    </span>
                  </td>
                  <td className={styles.tdName}>
                    {sp.salesperson_name || sp.salesperson_no}
                  </td>
                  <td className={styles.tdCustomers}>
                    <button
                      className={styles.customerLink}
                      onClick={() =>
                        navigate(`/portal/customers?salesperson_no=${sp.salesperson_no}`)
                      }
                    >
                      {sp.customer_count.toLocaleString()}
                    </button>
                  </td>
                  <td className={styles.tdAssignment}>
                    {sp.assigned_to ? (
                      <span className={styles.assignedBadge}>
                        {sp.assigned_to_name || sp.assigned_to}
                      </span>
                    ) : (
                      <span className={styles.unassignedLabel}>Unassigned</span>
                    )}
                  </td>
                  <td className={styles.tdActions}>
                    {sp.assigned_to === null ? (
                      <button
                        className={styles.btnClaim}
                        onClick={() => handleAssign(sp.salesperson_no)}
                        disabled={actionLoading === sp.salesperson_no}
                      >
                        {actionLoading === sp.salesperson_no ? 'Assigning...' : 'Claim'}
                      </button>
                    ) : (
                      <button
                        className={styles.btnUnassign}
                        onClick={() => handleUnassign(sp.salesperson_no)}
                        disabled={actionLoading === sp.salesperson_no}
                      >
                        {actionLoading === sp.salesperson_no ? 'Removing...' : 'Unassign'}
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Toast toasts={toasts} onRemove={removeToast} />
    </div>
  );
}
